import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  motion,
  animate,
  useInView,
  useMotionValue,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from 'framer-motion';

export const EASE = [0.22, 1, 0.36, 1];
export const EASE_SOFT = [0.16, 1, 0.3, 1];

/* ── Fade/slide/blur reveal on scroll ── */
export function Reveal({
  children, delay = 0, y = 30, blur = true, once = true,
  amount = 0.2, duration = 0.85, className, style,
}) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      style={style}
      initial={reduce ? false : { opacity: 0, y, filter: blur ? 'blur(8px)' : 'blur(0px)' }}
      whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
      viewport={{ once, amount, margin: '0px 0px -60px 0px' }}
      transition={{ duration, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

/* ── Word-by-word masked text reveal ── */
export function WordReveal({ text, className, style, delay = 0, stagger = 0.055, duration = 0.7, once = true }) {
  const reduce = useReducedMotion();
  const words = String(text).split(' ');
  /* NOTE: the viewport observer lives on the OUTER (visible) wrapper -
     the inner span starts fully clipped by its mask, so observing it
     directly would never fire. Variants propagate down to each word. */
  return (
    <motion.span
      className={className}
      style={style}
      aria-label={text}
      initial={reduce ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once, amount: 0.4 }}
    >
      {words.map((word, i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{ display: 'inline-block', overflow: 'hidden', verticalAlign: 'bottom', paddingBottom: '0.12em', marginBottom: '-0.12em' }}
        >
          <motion.span
            style={{ display: 'inline-block', willChange: 'transform' }}
            variants={{
              hidden: { y: '115%', rotate: 3 },
              visible: {
                y: '0%', rotate: 0,
                transition: { duration, delay: delay + i * stagger, ease: EASE },
              },
            }}
          >
            {word}
          </motion.span>
          {i < words.length - 1 ? ' ' : ''}
        </span>
      ))}
    </motion.span>
  );
}

/* ── Full-line masked reveal (for multi-line headings) ── */
export function MaskLine({ children, delay = 0, duration = 0.9, once = true, style, className }) {
  const reduce = useReducedMotion();
  return (
    <motion.span
      className={className}
      style={{ display: 'block', overflow: 'hidden', paddingBottom: '0.1em', marginBottom: '-0.1em', ...style }}
      initial={reduce ? false : 'hidden'}
      whileInView="visible"
      viewport={{ once, amount: 0.4 }}
    >
      <motion.span
        style={{ display: 'block', willChange: 'transform' }}
        variants={{
          hidden: { y: '110%' },
          visible: { y: '0%', transition: { duration, delay, ease: EASE } },
        }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
}

/* ── Magnetic hover wrapper for buttons ── */
export function Magnetic({ children, strength = 0.32, className, style }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 180, damping: 14, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 180, damping: 14, mass: 0.4 });
  const reduce = useReducedMotion();

  const onMove = (e) => {
    if (reduce) return;
    const rect = ref.current.getBoundingClientRect();
    x.set((e.clientX - rect.left - rect.width / 2) * strength);
    y.set((e.clientY - rect.top - rect.height / 2) * strength);
  };
  const onLeave = () => { x.set(0); y.set(0); };

  return (
    <motion.div
      ref={ref}
      className={className}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ display: 'inline-block', x: sx, y: sy, ...style }}
    >
      {children}
    </motion.div>
  );
}

/* ── Count-up number: parses "4M", "$1K", "22%", "5+" — falls back to plain render ── */
const NUM_RE = /^([^0-9]{0,4}?)(\d+(?:\.\d+)?)([A-Za-z%×+]{0,4})$/;

export function CountUp({ value, duration = 1.8, className, style, delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduce = useReducedMotion();
  const match = String(value).match(NUM_RE);
  const [display, setDisplay] = useState(match && !reduce ? `${match[1]}0${match[3]}` : String(value));

  useEffect(() => {
    if (!match || reduce || !inView) return;
    const target = parseFloat(match[2]);
    const decimals = (match[2].split('.')[1] || '').length;
    const controls = animate(0, target, {
      duration,
      delay,
      ease: EASE_SOFT,
      onUpdate: (v) => setDisplay(`${match[1]}${v.toFixed(decimals)}${match[3]}`),
    });
    return () => controls.stop();
  }, [inView]); // eslint-disable-line

  return <span ref={ref} className={className} style={style}>{display}</span>;
}

/* ── Scroll parallax ── */
export function Parallax({ children, speed = 0.12, className, style }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], [speed * -160, speed * 160]);
  return (
    <motion.div ref={ref} className={className} style={{ position: 'relative', y: reduce ? 0 : y, ...style }}>
      {children}
    </motion.div>
  );
}

/* ── Subtle 3D tilt on mouse move ── */
export function Tilt({ children, max = 5, className, style }) {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 220, damping: 18 });
  const sry = useSpring(ry, { stiffness: 220, damping: 18 });
  const reduce = useReducedMotion();

  const onMove = (e) => {
    if (reduce) return;
    const rect = ref.current.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5;
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    ry.set(px * max);
    rx.set(-py * max);
  };
  const onLeave = () => { rx.set(0); ry.set(0); };

  return (
    <motion.div
      ref={ref}
      className={className}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, transformPerspective: 900, transformStyle: 'preserve-3d', ...style }}
    >
      {children}
    </motion.div>
  );
}

/* ── Image reveal: scale-settle inside clipped container ── */
export function ImageReveal({ src, alt = '', radius = 20, className, style, imgStyle, once = true }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      style={{ overflow: 'hidden', borderRadius: radius, ...style }}
      initial={reduce ? false : { clipPath: 'inset(6% 4% 6% 4% round 24px)', opacity: 0 }}
      whileInView={{ clipPath: 'inset(0% 0% 0% 0% round 20px)', opacity: 1 }}
      viewport={{ once, amount: 0.25 }}
      transition={{ duration: 1, ease: EASE }}
    >
      <motion.img
        src={src}
        alt={alt}
        initial={reduce ? false : { scale: 1.14 }}
        whileInView={{ scale: 1 }}
        viewport={{ once, amount: 0.25 }}
        transition={{ duration: 1.2, ease: EASE }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', ...imgStyle }}
      />
    </motion.div>
  );
}

/* ── Page transition wrapper ── */
export function PageWrap({ children }) {
  useLayoutEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 26 }}
      animate={{ opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } }}
      exit={{ opacity: 0, y: -18, transition: { duration: 0.3, ease: [0.4, 0, 1, 1] } }}
    >
      {children}
    </motion.div>
  );
}

/* ── Section header with animated rule ── */
export function SectionHeader({ title, count }) {
  return (
    <div className="section-header" style={{ position: 'relative' }}>
      <Reveal y={14} blur={false} duration={0.6} style={{ display: 'inline-block' }}>
        <span className="section-title">{title}</span>
      </Reveal>
      <Reveal y={14} blur={false} duration={0.6} delay={0.1} style={{ display: 'inline-block' }}>
        <span className="section-count">{count}</span>
      </Reveal>
      <motion.span
        aria-hidden="true"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        viewport={{ once: true, amount: 1 }}
        transition={{ duration: 1.1, ease: EASE, delay: 0.15 }}
        style={{
          position: 'absolute', left: 0, right: 0, bottom: -12, height: 1,
          background: 'rgba(14,14,12,0.15)', transformOrigin: 'left', display: 'block',
        }}
      />
    </div>
  );
}
